/** Operator-side Q&A panel inside SessionPage — Phase B of
 * CAMPAIGN_LEADS_QUESTIONS.md.
 *
 * Mounts in the right sidebar next to the chat. One card per question,
 * field renderer dispatched by kind. Each answer auto-saves on blur
 * (text/number) or on change (dropdown/radio/checkbox/boolean/rating/
 * date). A small "X of N answered" counter sits in the header so the
 * operator can see completion at a glance.
 *
 * Backlog 2026-09-18 — one call, one beneficiary, but a beneficiary can
 * have several project sites (`sites` in the GET payload — empty for
 * the ordinary single-site case, unchanged from before this existed).
 * When sites are present, the FULL question list repeats once per site
 * under its own heading (one long scrollable list, not tabs — grouping
 * by site headers is the whole point: the operator scrolls through
 * "Site A"'s questions, then "Site B"'s, in one pass).
 *
 * Field user does NOT see this — it's the operator's evidence-capture
 * surface. Read perm is `session.read` (same as the rest of the page);
 * write is `session.qa.write` (default-on for inspector + org_admin).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { MuiDatePicker } from "@/components/MuiDatePicker";
import { ApiPaths, api } from "@/lib/api";
import {
  BOOLEANISH_KINDS,
  COMMENT_KINDS,
  TRISTATE_VALUES,
} from "@/lib/types";
import type {
  Address,
  InspectionAnswer,
  Question,
  QuestionKind,
} from "@/lib/types";


type QaPayload = {
  questions: Question[];
  sites: Address[];
  answers: InspectionAnswer[];
};


type WritePayload = {
  value_text?: string;
  value_number?: string | null;
  value_json?: string[];
  /** The "+ comment" kinds only. Every save must include BOTH this and
   *  value_text together — the PUT fully replaces the row's kind slots
   *  each time, so a yes/no-only save would otherwise wipe an existing
   *  comment (and vice versa). */
  comment?: string;
};

/** Composite key so a question's answer can be looked up per-site
 *  without a second Map per site. `site` is `""` (not `null`) for the
 *  ungrouped case — Map keys need a stable primitive. */
function answerKey(questionId: string, siteId: string | null): string {
  return `${questionId}:${siteId ?? ""}`;
}


type Props = {
  sessionId: string;
  /** When set, the panel renders as a compact translucent overlay
   *  (e.g. bottom-right corner of the video stage) instead of as a
   *  full-width card in the sidebar. Collapsed state is unchanged —
   *  the panel still defaults closed and only surfaces when questions
   *  exist for the campaign. */
  floating?: boolean;
};


export function InspectionQaPanel({ sessionId, floating = false }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const qa = useQuery({
    queryKey: ["session-qa", sessionId],
    queryFn: async () => {
      const res = await api.get<QaPayload>(ApiPaths.sessionQa(sessionId));
      return res.data;
    },
  });

  const save = useMutation({
    mutationFn: async ({
      questionId,
      payload,
    }: {
      questionId: string;
      payload: WritePayload & { site?: string | null };
    }) => {
      const res = await api.put<InspectionAnswer>(
        ApiPaths.sessionQaAnswer(sessionId, questionId),
        payload,
      );
      return res.data;
    },
    onSuccess: (next) => {
      // Patch the cached payload — re-fetching the full list on every
      // keystroke would flicker the panel. Match on (question, site) —
      // a save for one site must not evict another site's cached
      // answer to the SAME question.
      qc.setQueryData<QaPayload | undefined>(
        ["session-qa", sessionId],
        (cur) => {
          if (!cur) return cur;
          const without = cur.answers.filter(
            (a) => !(a.question === next.question && a.site === next.site),
          );
          return { ...cur, answers: [...without, next] };
        },
      );
    },
    // Without onError the 403 silently dropped the answer + left the
    // UI showing the stale value. Surface the server's `detail` so
    // the operator sees *why* the save failed instead of guessing the
    // network ate the request.
    onError: () => {
      // Force a refetch so the visible value re-syncs to whatever the
      // server actually persisted (likely unchanged on a 403).
      void qc.invalidateQueries({ queryKey: ["session-qa", sessionId] });
    },
  });

  // Collapse state — persisted per-session in localStorage so the
  // operator's choice survives reloads + navigation between sessions.
  // Defaults to COLLAPSED: the panel was previously always-on and
  // dominated the sidebar even when the session hadn't started.
  const storageKey = `nb_qa_open_${sessionId}`;
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, open ? "1" : "0");
    } catch {
      /* SSR / locked storage / quota — non-fatal */
    }
  }, [open, storageKey]);

  if (qa.isLoading) {
    return null; // collapsed-by-default → invisible
  }
  if (!qa.data) return null;
  const { questions, answers } = qa.data;
  // Defensive default — a stale cached response (or any other caller
  // of this endpoint) predating this field simply behaves as the
  // ordinary single-site case rather than throwing on `.length`.
  const sites = qa.data.sites ?? [];
  if (questions.length === 0) return null;

  const byQuestionSite = new Map(
    answers.map((a) => [answerKey(a.question ?? "", a.site), a]),
  );
  const answeredCount = answers.filter((a) => isAnswered(a)).length;
  // No sites → one slot per question, exactly as before this feature
  // existed. Sites present → the full question set repeats per site.
  const totalSlots = sites.length === 0
    ? questions.length
    : questions.length * sites.length;
  const complete = answeredCount === totalSlots;

  // Floating overlay: translucent backdrop + blur so the video shows
  // through, capped width so it doesn't dominate the stage, and a
  // bounded max-height with scroll so a long questionnaire stays
  // usable on small viewports.
  const wrapperClass = floating
    ? "rounded-lg border border-nb-border bg-nb-bg/90 p-3 shadow-lg backdrop-blur-sm space-y-2 w-80 max-w-[calc(100vw-2rem)]"
    : "card space-y-2 p-3";

  // 2026-09-28, reported from a live call: even collapsed, a 20rem-wide
  // header bar parked over the video is a lot of furniture, and on a
  // narrow stage it still crowded the media controls. Collapsed, the
  // floating panel is now a single round button — it holds one corner
  // instead of a whole band, and the questionnaire opens on tap.
  //
  // The counter rides the button as a progress ring rather than as
  // text: at 56px there is no room for "3 / 15" that anyone could read
  // mid-call, but "how much is left" is exactly what the operator
  // glances down for. The ring is drawn from the same answeredCount /
  // totalSlots the expanded header prints, so the two cannot disagree.
  if (floating && !open) {
    const pct = totalSlots > 0 ? answeredCount / totalSlots : 0;
    const R = 26;
    const C = 2 * Math.PI * R;
    const label = t("inspectionQa.openBubble", {
      done: answeredCount,
      total: totalSlots,
    });
    return (
      <button
        type="button"
        data-testid="qa-bubble"
        onClick={() => setOpen(true)}
        aria-expanded={false}
        aria-controls={`qa-body-${sessionId}`}
        title={label}
        aria-label={label}
        className="group relative grid h-14 w-14 place-items-center rounded-full border border-nb-border bg-nb-bg/90 shadow-lg backdrop-blur-sm transition hover:border-nb-mint focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nb-mint"
      >
        {/* Progress ring. -rotate-90 puts 0% at 12 o'clock; without it
            the arc starts at 3 o'clock and reads as a random wedge. */}
        <svg
          className="pointer-events-none absolute inset-0 -rotate-90"
          viewBox="0 0 56 56"
          aria-hidden
        >
          <circle
            cx="28"
            cy="28"
            r={R}
            fill="none"
            strokeWidth="3"
            className="stroke-nb-border"
          />
          <circle
            cx="28"
            cy="28"
            r={R}
            fill="none"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - pct)}
            className={
              complete
                ? "stroke-cvp-ok transition-[stroke-dashoffset] duration-300"
                : "stroke-nb-mint transition-[stroke-dashoffset] duration-300"
            }
          />
        </svg>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="pointer-events-none text-nb-text-2 transition-colors group-hover:text-nb-text"
          aria-hidden
        >
          <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
          <rect x="9" y="3" width="6" height="4" rx="1" />
          <path d="m9 14 2 2 4-4" />
        </svg>
        {/* Remaining-count badge. Hidden once every slot is answered —
            a "0" badge invites a second look for nothing. */}
        {!complete && (
          <span className="pointer-events-none absolute -right-1 -top-1 grid min-w-[1.25rem] place-items-center rounded-full bg-nb-mint px-1 text-[11px] font-semibold leading-4 text-nb-bg tabular-nums">
            {totalSlots - answeredCount}
          </span>
        )}
      </button>
    );
  }
  const bodyMaxHeight = floating
    ? "max-h-[60vh] overflow-y-auto pr-1"
    : "";

  return (
    <section className={wrapperClass}>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`qa-body-${sessionId}`}
      >
        <h3 className="text-xs font-semibold uppercase tracking-wide text-nb-text-2">
          {t("inspectionQa.panelTitle")}
        </h3>
        <span className="flex items-center gap-2">
          <span
            className={`text-xs ${
              complete ? "text-cvp-ok" : "text-nb-text-3"
            }`}
          >
            {t("inspectionQa.completionCounter", {
              done: answeredCount,
              total: totalSlots,
            })}
          </span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`text-nb-text-3 transition-transform ${
              open ? "rotate-180" : ""
            }`}
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>
      {open && (
        <div
          id={`qa-body-${sessionId}`}
          className={`space-y-3 ${bodyMaxHeight}`}
        >
          {sites.length === 0 ? (
            <ol className="space-y-3">
              {questions.map((q) => (
                <QuestionRow
                  key={q.id}
                  question={q}
                  answer={byQuestionSite.get(answerKey(q.id, null)) ?? null}
                  saving={save.isPending}
                  onSave={(payload) =>
                    save.mutate({
                      questionId: q.id,
                      payload: { ...payload, site: null },
                    })
                  }
                />
              ))}
            </ol>
          ) : (
            <SiteStepper
              sites={sites}
              questions={questions}
              answerFor={(qid, siteId) =>
                byQuestionSite.get(answerKey(qid, siteId)) ?? null
              }
              saving={save.isPending}
              onSave={(questionId, payload, siteId) =>
                save.mutate({
                  questionId,
                  payload: { ...payload, site: siteId },
                })
              }
            />
          )}
          {save.isError && (
            <p className="text-xs text-cvp-danger">
              {extractApiError(save.error) ??
                t("inspectionQa.saveErrorGeneric")}
            </p>
          )}
        </div>
      )}
    </section>
  );
}


function extractApiError(err: unknown): string | null {
  // Axios stamps the parsed JSON body on `response.data`. Most of our
  // endpoints return `{"detail": "..."}` (DRF default + our gates).
  // Fall back to the axios Error.message for transport-level failures.
  const e = err as {
    response?: { data?: { detail?: string } };
    message?: string;
  };
  return e?.response?.data?.detail ?? e?.message ?? null;
}


function isAnswered(a: InspectionAnswer): boolean {
  if (a.value_text && a.value_text.trim()) return true;
  if (a.value_number !== null && a.value_number !== "") return true;
  if (Array.isArray(a.value_json) && a.value_json.length > 0) return true;
  return false;
}


function QuestionRow({
  question,
  answer,
  saving,
  onSave,
}: {
  question: Question;
  answer: InspectionAnswer | null;
  saving: boolean;
  onSave: (payload: WritePayload) => void;
}) {
  return (
    <li className="space-y-1.5 border-l-2 border-nb-border pl-3">
      <div>
        <p className="text-sm text-nb-text">
          {question.label}
          {question.required && (
            <span className="ml-0.5 text-cvp-warn">*</span>
          )}
        </p>
        {question.help_text && (
          <p className="text-xs text-nb-text-3">{question.help_text}</p>
        )}
      </div>
      <FieldRenderer
        question={question}
        answer={answer}
        disabled={saving}
        onSave={onSave}
      />
    </li>
  );
}


function FieldRenderer({
  question,
  answer,
  disabled,
  onSave,
}: {
  question: Question;
  answer: InspectionAnswer | null;
  disabled: boolean;
  onSave: (payload: WritePayload) => void;
}) {
  const { t } = useTranslation();
  const kind: QuestionKind = question.kind;

  if (kind === "text") {
    return (
      <BlurInput
        value={answer?.value_text ?? ""}
        placeholder={question.placeholder}
        disabled={disabled}
        onCommit={(v) => onSave({ value_text: v })}
      />
    );
  }
  if (kind === "textarea") {
    return (
      <BlurTextarea
        value={answer?.value_text ?? ""}
        placeholder={question.placeholder}
        disabled={disabled}
        onCommit={(v) => onSave({ value_text: v })}
      />
    );
  }
  if (kind === "number") {
    return (
      <BlurInput
        type="number"
        value={answer?.value_number ?? ""}
        placeholder={question.placeholder}
        disabled={disabled}
        step={question.step ?? undefined}
        min={question.min_value ?? undefined}
        max={question.max_value ?? undefined}
        onCommit={(v) =>
          onSave({ value_number: v.trim() === "" ? null : v.trim() })
        }
      />
    );
  }
  if (kind === "dropdown") {
    return (
      <select
        className="input"
        value={answer?.value_text ?? ""}
        disabled={disabled}
        onChange={(e) => onSave({ value_text: e.target.value })}
      >
        <option value="">—</option>
        {question.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  }
  if (kind === "radio") {
    return (
      <div className="flex flex-wrap gap-3 text-sm text-nb-text">
        {question.options.map((o) => (
          <label key={o.value} className="flex items-center gap-1">
            <input
              type="radio"
              name={`q-${question.id}`}
              value={o.value}
              checked={answer?.value_text === o.value}
              disabled={disabled}
              onChange={() => onSave({ value_text: o.value })}
            />
            {o.label}
          </label>
        ))}
      </div>
    );
  }
  if (kind === "checkboxes") {
    const current = new Set(answer?.value_json ?? []);
    return (
      <div className="flex flex-wrap gap-3 text-sm text-nb-text">
        {question.options.map((o) => (
          <label key={o.value} className="flex items-center gap-1">
            <input
              type="checkbox"
              checked={current.has(o.value)}
              disabled={disabled}
              onChange={(e) => {
                const next = new Set(current);
                if (e.target.checked) next.add(o.value);
                else next.delete(o.value);
                onSave({ value_json: [...next] });
              }}
            />
            {o.label}
          </label>
        ))}
      </div>
    );
  }
  // All four Yes/No kinds share one branch. They differ in exactly two
  // ways — whether "I don't know" is offered, and whether a comment box
  // is — so four near-identical blocks would be four places for the next
  // change to miss one.
  if (BOOLEANISH_KINDS.includes(kind)) {
    const choice = answer?.value_text ?? "";
    const comment = answer?.comment ?? "";
    const tri = kind === "tristate" || kind === "tristate_comment";
    const values: readonly string[] = tri ? TRISTATE_VALUES : ["yes", "no"];
    const hasComment = COMMENT_KINDS.includes(kind);
    // "always" (the default) and blank both show the box
    // unconditionally. A designer can still gate it on one answer —
    // "only on no", so a plain "yes" doesn't get an empty box — but
    // that is an opt-in.
    //
    // Blank must mean ALWAYS, not never: the old gate required a
    // trigger to be set AND matched, so a question with no trigger had
    // a comment box that could never be reached, and the operator had
    // no way to tell. A comment box you cannot open is worse than no
    // comment box.
    const trigger = question.comment_trigger || "always";
    const showComment =
      hasComment && (trigger === "always" || choice === trigger);
    const label = (v: string) =>
      v === "yes"
        ? t("common.yes", "Yes")
        : v === "no"
          ? t("common.no", "No")
          : t("inspectionQa.dontKnow", "Don't know");
    return (
      <div className="space-y-1.5">
        <div className="flex flex-wrap gap-3 text-sm text-nb-text">
          {values.map((v) => (
            <label key={v} className="flex items-center gap-1">
              <input
                type="radio"
                name={`q-${question.id}`}
                value={v}
                checked={choice === v}
                disabled={disabled}
                // A comment kind carries BOTH slots on every save —
                // see WritePayload.comment.
                onChange={() =>
                  onSave(
                    hasComment
                      ? { value_text: v, comment }
                      : { value_text: v },
                  )
                }
              />
              {label(v)}
            </label>
          ))}
        </div>
        {showComment && (
          <BlurTextarea
            value={comment}
            placeholder={question.placeholder}
            disabled={disabled}
            onCommit={(v) => onSave({ value_text: choice, comment: v })}
          />
        )}
      </div>
    );
  }
  if (kind === "date") {
    return (
      <MuiDatePicker
        value={answer?.value_text ?? ""}
        disabled={disabled}
        onChange={(v) => onSave({ value_text: v })}
      />
    );
  }
  if (kind === "rating") {
    const value = answer?.value_number
      ? parseInt(answer.value_number, 10)
      : 0;
    return (
      <div className="flex gap-1 text-lg">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            className={
              value >= n ? "text-nb-mint" : "text-nb-text-3 hover:text-nb-text-2"
            }
            disabled={disabled}
            onClick={() => onSave({ value_number: String(n) })}
            aria-label={`rate ${n}`}
          >
            ★
          </button>
        ))}
      </div>
    );
  }
  return null;
}


/** <input> that commits on blur — avoids saving on every keystroke. */
function BlurInput({
  value: initial,
  onCommit,
  disabled,
  ...rest
}: {
  value: string;
  onCommit: (v: string) => void;
  disabled?: boolean;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "onChange" | "value" | "disabled">) {
  const [local, setLocal] = useState(initial);
  useEffect(() => {
    setLocal(initial);
  }, [initial]);
  return (
    <input
      className="input text-sm"
      disabled={disabled}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (local !== initial) onCommit(local);
      }}
      {...rest}
    />
  );
}


function BlurTextarea({
  value: initial,
  onCommit,
  disabled,
  placeholder,
}: {
  value: string;
  onCommit: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [local, setLocal] = useState(initial);
  useEffect(() => {
    setLocal(initial);
  }, [initial]);
  return (
    <textarea
      className="input min-h-[5rem] text-sm"
      disabled={disabled}
      value={local}
      placeholder={placeholder}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (local !== initial) onCommit(local);
      }}
    />
  );
}

/** One site at a time, with the option to carry the previous site's
 *  answers over.
 *
 *  The questionnaire repeats per site by design — a finding belongs to
 *  the building it was observed in. But 15 questions across a
 *  beneficiary's sites is 75 fields at five sites and 180 at twelve,
 *  presented as one unbroken scroll with nothing showing how much was
 *  left. Measured across every closed multi-site call: 148 answers
 *  recorded of 1,035 slots offered. 14%.
 *
 *  So two things, and only the second of them saves any typing:
 *
 *  - one site's questions at a time, with "Site 2 of 5" and a progress
 *    bar. This makes the work legible, not shorter — an operator can
 *    see the end of ONE site, and see which site they are answering
 *    for, which the single scroll never made obvious.
 *  - "copy from the previous site", because these retrofits are the
 *    same installer, the same invoice and the same fittings across a
 *    farm's buildings. It is an explicit button rather than an
 *    automatic pre-fill: copying is a claim about a building, and it
 *    should take a deliberate press.
 *
 *  Single-site beneficiaries — 697 of 1,011 — never see any of this;
 *  that branch is untouched above.
 */
function SiteStepper({
  sites,
  questions,
  answerFor,
  saving,
  onSave,
}: {
  sites: Address[];
  questions: Question[];
  answerFor: (questionId: string, siteId: string) => InspectionAnswer | null;
  saving: boolean;
  onSave: (
    questionId: string,
    payload: WritePayload,
    siteId: string,
  ) => void;
}) {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const site = sites[Math.min(index, sites.length - 1)];
  const previous = index > 0 ? sites[index - 1] : undefined;

  if (!site) return null;
  // Bound after the guard: TS narrowing does not reach into the
  // copy closure below.
  const siteId = site.id;

  const filled = (questionId: string, siteId: string) => {
    const a = answerFor(questionId, siteId);
    return a !== null && isAnswered(a);
  };
  const answeredHere = questions.filter((q) => filled(q.id, site.id)).length;
  const doneFor = (s: Address) =>
    questions.every((q) => filled(q.id, s.id));

  // Only offer the copy when there is something to copy FROM and this
  // site is still empty. Offering it over existing answers would invite
  // overwriting work already done on this building.
  const canCopy =
    !!previous &&
    answeredHere === 0 &&
    questions.some((q) => filled(q.id, previous.id));

  function copyFromPrevious() {
    if (!previous) return;
    for (const q of questions) {
      const src = answerFor(q.id, previous.id);
      if (!src || !isAnswered(src)) continue;
      onSave(
        q.id,
        {
          value_text: src.value_text,
          value_number: src.value_number,
          value_json: src.value_json,
          comment: src.comment,
        },
        siteId,
      );
    }
  }

  return (
    <div className="space-y-2" data-testid="qa-site-stepper">
      <div className="flex items-baseline justify-between gap-2">
        <h4 className="min-w-0 truncate text-xs font-semibold text-nb-text">
          {site.label || site.formatted}
        </h4>
        <span className="shrink-0 text-[11px] tabular-nums text-nb-text-3">
          {t("inspectionQa.siteStep", {
            index: index + 1,
            total: sites.length,
          })}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <div className="h-1 flex-1 overflow-hidden rounded bg-nb-border">
          <div
            className="h-full bg-nb-mint transition-[width]"
            style={{
              width: `${Math.round(
                (answeredHere / Math.max(questions.length, 1)) * 100,
              )}%`,
            }}
          />
        </div>
        <span className="text-[11px] tabular-nums text-nb-text-3">
          {answeredHere}/{questions.length}
        </span>
      </div>

      {/* Which sites are finished, and a way to jump. Five dots beat a
          scrollbar for "how much of this is left". */}
      <div className="flex flex-wrap gap-1">
        {sites.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={t("inspectionQa.goToSite", { index: i + 1 })}
            aria-current={i === index}
            className={`h-1.5 flex-1 min-w-[10px] rounded transition ${
              i === index
                ? "bg-nb-mint"
                : doneFor(s)
                  ? "bg-nb-mint/40"
                  : "bg-nb-border"
            }`}
          />
        ))}
      </div>

      {canCopy && (
        <button
          type="button"
          onClick={copyFromPrevious}
          disabled={saving}
          data-testid="qa-copy-previous"
          className="w-full rounded border border-nb-border px-2 py-1.5 text-[11px] text-nb-text-2 transition hover:border-nb-mint hover:text-nb-mint disabled:opacity-50"
        >
          {t("inspectionQa.copyFromPrevious", {
            site: previous?.label || previous?.formatted || "",
          })}
        </button>
      )}

      <ol className="space-y-3">
        {questions.map((q) => (
          <QuestionRow
            key={`${site.id}-${q.id}`}
            question={q}
            answer={answerFor(q.id, site.id)}
            saving={saving}
            onSave={(payload) => onSave(q.id, payload, site.id)}
          />
        ))}
      </ol>

      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
          className="flex-1 rounded border border-nb-border px-2 py-1.5 text-[11px] text-nb-text-2 disabled:opacity-40"
        >
          {t("inspectionQa.prevSite")}
        </button>
        <button
          type="button"
          onClick={() =>
            setIndex((i) => Math.min(sites.length - 1, i + 1))
          }
          disabled={index >= sites.length - 1}
          data-testid="qa-next-site"
          className="flex-1 rounded bg-nb-mint px-2 py-1.5 text-[11px] font-semibold text-nb-bg disabled:opacity-40"
        >
          {t("inspectionQa.nextSite")}
        </button>
      </div>
    </div>
  );
}
